import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { mutate as mutateCache } from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useImportWizardRealtimeContext,
  useImportWizardSnapshotContext,
  useImportWizardStableContext,
} from '../context/ImportWizardContext'
import {
  getAudienceImportFlowSnapshotKey,
  getAudienceImportIdentityShellKey,
  getAudienceImportSessionKey,
} from '../lib/cache-keys'
import { IDENTITY_FLOW_SNAPSHOT_REVALIDATION_POLICY } from '../lib/flow-snapshot-revalidation-policy'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { shouldRevalidateFlowSnapshotForImportSessionUpdate } from '../lib/import-session-update-events'
import type {
  AudienceImportRouteAck,
  AudienceImportThinRouteAck,
  AudienceImportSession,
  IdentityFieldPatchRequest,
  IdentityFieldPatchResponse,
  IdentityStepShell,
} from '@/types/components/audiences/import-session'
import {
  getRouteAckFromImportSessionEvent,
  mergeMachineVersionIntoFlowSnapshot,
  mergeMachineVersionIntoSession,
  mergeRouteAckIntoIdentityShell,
  preferNewerFlowSnapshot,
} from '../lib/import-session-deltas'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'

const IDENTITY_SHELL_IGNORED_WEBSOCKET_REASONS = new Set([
  'preview_hydration_completed',
  'preview_twin_enriched',
])

type AudienceImportAnyRouteAck =
  | AudienceImportRouteAck
  | AudienceImportThinRouteAck

type IdentityMutationKind =
  | 'patch_field'
  | 'complete_required_fields'
  | 'discard'

function shouldRefreshIdentityShellFromSnapshot(
  snapshot: AudienceCreationFlowSnapshot | null | undefined,
  shell: IdentityStepShell | undefined,
) {
  if (!snapshot || !shell) {
    return false
  }

  const snapshotVersion = snapshot.machineVersion
  if (
    typeof snapshotVersion !== 'number' ||
    !Number.isFinite(snapshotVersion) ||
    snapshotVersion <= shell.machineVersion
  ) {
    return false
  }

  const requiredFieldsRoute = snapshot.links?.requiredFields?.trim() ?? null
  const currentRoute = snapshot.route?.trim() ?? null
  const isRequiredFieldsRoute =
    requiredFieldsRoute != null && currentRoute === requiredFieldsRoute
  const identityState = snapshot.branchSummaries?.identity?.state

  return (
    snapshot.wizardPhase !== 'analyze_running' &&
    (shell.status === 'analyzing' ||
      isRequiredFieldsRoute ||
      identityState === 'needs_review')
  )
}

function mergeIdentityPatchIntoShell(
  current: IdentityStepShell | null | undefined,
  response: IdentityFieldPatchResponse,
): IdentityStepShell | null | undefined {
  if (!current) {
    return current
  }

  const merged: IdentityStepShell = {
    ...current,
    machineVersion: response.machineVersion,
    requiredFields: response.requiredFields ?? current.requiredFields,
    activeWorkspace: Object.prototype.hasOwnProperty.call(
      response,
      'activeWorkspace',
    )
      ? (response.activeWorkspace ?? null)
      : response.requiredFields?.readyToCreate === true
        ? null
      : (current.activeWorkspace ?? null),
  }

  if (!response.routeAck) {
    return merged
  }

  return mergeRouteAckIntoIdentityShell(merged, response.routeAck) ?? merged
}

export function useIdentityStep(sessionId: string) {
  const { snapshot } = useImportWizardSnapshotContext()
  const { isRealtimeConnected } = useImportWizardRealtimeContext()
  const { registerImportSessionEventHandler, setLastMutationRequestId } =
    useImportWizardStableContext()
  const [activeMutation, setActiveMutation] = useState<IdentityMutationKind | null>(
    null,
  )
  const [lastPatchRequestId, setLastPatchRequestId] = useState<string | null>(null)
  const mutationInFlightRef = useRef(false)
  const pendingShellInvalidationRef = useRef(false)
  const snapshotDrivenRefreshVersionRef = useRef<number | null>(null)

  useEffect(() => {
    mutationInFlightRef.current = false
    snapshotDrivenRefreshVersionRef.current = null
    setActiveMutation(null)
    setLastPatchRequestId(null)
  }, [sessionId])

  const shellCacheKey = useMemo(
    () => getAudienceImportIdentityShellKey(sessionId),
    [sessionId],
  )
  const shellQuery = useSWR<IdentityStepShell>(
    shellCacheKey,
    (_key: unknown, options?: { signal?: AbortSignal }) =>
      audienceImportSessionsApi.getIdentityShell(sessionId, options?.signal),
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 1500),
      revalidateOnFocus: activeMutation === null,
      dedupingInterval: 5000,
    },
  )
  const mutateShell = shellQuery.mutate
  const shellData = shellQuery.data
  const shellIsValidating = shellQuery.isValidating
  const currentShellMachineVersion = shellData?.machineVersion ?? null

  const flowSnapshotCacheKey = useMemo(
    () => getAudienceImportFlowSnapshotKey(sessionId),
    [sessionId],
  )
  const sessionCacheKey = useMemo(
    () => getAudienceImportSessionKey(sessionId),
    [sessionId],
  )

  const revalidateShell = useCallback(
    async (source: string) => {
      incrementImportSessionClientMetric('identity_shell_revalidate', {
        sessionId,
        hook: 'identity',
        source,
      })
      await mutateShell()
    },
    [mutateShell, sessionId],
  )

  const revalidateFlowSnapshot = useCallback(async (source: string) => {
    if (!flowSnapshotCacheKey) {
      return
    }
    incrementImportSessionClientMetric('flow_snapshot_revalidate', {
      sessionId,
      hook: 'identity',
      source,
    })
    await mutateCache(
      flowSnapshotCacheKey,
      async (current) =>
        preferNewerFlowSnapshot(
          current as AudienceCreationFlowSnapshot | null | undefined,
          await audienceImportSessionsApi.getFlowSnapshot(sessionId, undefined),
        ),
      { revalidate: false, populateCache: true },
    )
  }, [flowSnapshotCacheKey, sessionId])

  const clearFlowSnapshot = useCallback(async () => {
    if (!flowSnapshotCacheKey) {
      return
    }
    await mutateCache(flowSnapshotCacheKey, undefined, { revalidate: false })
  }, [flowSnapshotCacheKey])

  const setCachedFlowSnapshot = useCallback(
    async (
      nextSnapshot: AudienceCreationFlowSnapshot | null | undefined,
      source: string,
    ) => {
      if (!flowSnapshotCacheKey) {
        return
      }
      incrementImportSessionClientMetric('flow_snapshot_local_update', {
        sessionId,
        hook: 'identity',
        source,
      })
      if (nextSnapshot == null) {
        await mutateCache(flowSnapshotCacheKey, nextSnapshot, {
          revalidate: false,
        })
        return
      }
      await mutateCache(
        flowSnapshotCacheKey,
        (current) =>
          preferNewerFlowSnapshot(
            current as AudienceCreationFlowSnapshot | null | undefined,
            nextSnapshot,
          ),
        {
          revalidate: false,
        },
      )
    },
    [flowSnapshotCacheKey, sessionId],
  )

  const applyMachineVersionFromIdentityPatch = useCallback(
    async (machineVersion: number | undefined, source: string) => {
      if (typeof machineVersion !== 'number' || !Number.isFinite(machineVersion)) {
        return
      }
      incrementImportSessionClientMetric('flow_snapshot_local_update', {
        sessionId,
        hook: 'identity',
        source,
      })
      const operations: Array<Promise<unknown>> = []
      if (flowSnapshotCacheKey) {
        operations.push(
          mutateCache(
            flowSnapshotCacheKey,
            (current) =>
              mergeMachineVersionIntoFlowSnapshot(
                current as AudienceCreationFlowSnapshot | null | undefined,
                machineVersion,
              ),
            { revalidate: false },
          ),
        )
      }
      if (sessionCacheKey) {
        operations.push(
          mutateCache(
            sessionCacheKey,
            (current) =>
              mergeMachineVersionIntoSession(
                current as AudienceImportSession | null | undefined,
                machineVersion,
              ),
            { revalidate: false },
          ),
        )
      }
      await Promise.all(operations)
    },
    [flowSnapshotCacheKey, sessionCacheKey, sessionId],
  )

  const cacheRouteAck = useCallback(
    async (ack: AudienceImportAnyRouteAck, source: string) => {
      const operations: Array<Promise<unknown>> = [
        setCachedFlowSnapshot(ack.flowSnapshot, source),
      ]
      if (shellData) {
        operations.push(
          mutateShell(
            (current) => {
              const merged = mergeRouteAckIntoIdentityShell(current ?? shellData, ack)
              return merged ?? current ?? shellData
            },
            { revalidate: false },
          ),
        )
      } else {
        operations.push(revalidateShell(`${source}:route_ack_no_cached_shell`))
      }
      await Promise.all(operations)
    },
    [mutateShell, revalidateShell, setCachedFlowSnapshot, shellData],
  )

  const syncFlowSnapshotForMutation = useCallback(
    async (
      mutationKind: IdentityMutationKind,
    ) => {
      const policy = IDENTITY_FLOW_SNAPSHOT_REVALIDATION_POLICY[mutationKind]
      switch (policy.updateStrategy) {
        case 'clear':
          await clearFlowSnapshot()
          return
        case 'server_revalidate':
        default:
          await revalidateFlowSnapshot(mutationKind)
      }
    },
    [
      clearFlowSnapshot,
      revalidateFlowSnapshot,
    ],
  )

  const queueInvalidation = useCallback(() => {
    pendingShellInvalidationRef.current = true
  }, [])

  const flushPendingInvalidation = useCallback(async () => {
    if (mutationInFlightRef.current) {
      return
    }
    if (!pendingShellInvalidationRef.current) {
      return
    }
    pendingShellInvalidationRef.current = false
    await revalidateShell('pending_invalidation')
  }, [revalidateShell])

  const handleSessionInvalidation = useCallback(async (event: ImportSessionUpdatedEvent) => {
    const ack = getRouteAckFromImportSessionEvent(event)
    const currentShellVersion = currentShellMachineVersion
    if (
      !event.identityShell &&
      !ack &&
      (
        IDENTITY_SHELL_IGNORED_WEBSOCKET_REASONS.has(event.reason) ||
        !shouldRevalidateFlowSnapshotForImportSessionUpdate(event)
      )
    ) {
      return
    }
    if (mutationInFlightRef.current) {
      queueInvalidation()
      return
    }
    const source = `websocket:${event.reason}`
    if (event.identityShell || ack) {
      const operations: Array<Promise<unknown>> = []
      if (event.identityShell) {
        operations.push(
          mutateShell(event.identityShell, {
            revalidate: false,
          }),
        )
      }
      if (ack) {
        operations.push(setCachedFlowSnapshot(ack.flowSnapshot, source))
        if (!event.identityShell) {
          if (shellData) {
            operations.push(
              mutateShell(
                (current) => {
                  const merged = mergeRouteAckIntoIdentityShell(
                    current ?? shellData,
                    ack,
                  )
                  return merged ?? current ?? shellData
                },
                { revalidate: false },
              ),
            )
            if (
              event.reason !== 'analyze_completed' &&
              typeof ack.machineVersion === 'number' &&
              Number.isFinite(ack.machineVersion) &&
              typeof currentShellVersion === 'number' &&
              ack.machineVersion > currentShellVersion
            ) {
              operations.push(
                revalidateShell(`${source}:route_ack_version_bump`),
              )
            }
          } else {
            operations.push(
              revalidateShell(`${source}:route_ack_no_cached_shell`),
            )
          }
        }
      }
      if (event.reason === 'analyze_completed' && !event.identityShell) {
        operations.push(revalidateShell(`${source}:required_fields`))
      }
      void Promise.allSettled(operations)
      return
    }
    void revalidateShell(source).catch(() => undefined)
  }, [
    currentShellMachineVersion,
    mutateShell,
    queueInvalidation,
    revalidateShell,
    setCachedFlowSnapshot,
    shellData,
  ])

  useEffect(() => {
    return registerImportSessionEventHandler(handleSessionInvalidation)
  }, [registerImportSessionEventHandler, handleSessionInvalidation])

  useEffect(() => {
    if (mutationInFlightRef.current || shellIsValidating) {
      return
    }
    if (!shouldRefreshIdentityShellFromSnapshot(snapshot, shellData)) {
      return
    }

    const nextVersion = snapshot?.machineVersion
    if (
      typeof nextVersion !== 'number' ||
      !Number.isFinite(nextVersion) ||
      snapshotDrivenRefreshVersionRef.current === nextVersion
    ) {
      return
    }

    snapshotDrivenRefreshVersionRef.current = nextVersion
    void revalidateShell('flow_snapshot_transition').catch(() => {
      if (snapshotDrivenRefreshVersionRef.current === nextVersion) {
        snapshotDrivenRefreshVersionRef.current = null
      }
    })
  }, [revalidateShell, shellData, shellIsValidating, snapshot])

  async function refetchOnConflict(error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 409) {
      await Promise.all([
        revalidateShell('conflict'),
        revalidateFlowSnapshot('conflict'),
      ])
    }
  }

  async function runExclusiveMutation<T>(
    mutationKind: IdentityMutationKind,
    action: () => Promise<T>,
  ) {
    if (mutationInFlightRef.current) {
      throw new Error('Another import session action is already in progress.')
    }

    mutationInFlightRef.current = true
    setActiveMutation(mutationKind)
    try {
      return await action()
    } finally {
      mutationInFlightRef.current = false
      setActiveMutation(null)
      void flushPendingInvalidation()
    }
  }

  function requireMachineVersion(): number {
    const v = shellQuery.data?.machineVersion
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      throw new Error(
        'Identity step mutation requires a loaded machine version. ' +
          'Ensure the identity shell is loaded before patching or discarding.',
      )
    }
    return v
  }

  async function patchField(
    canonicalId: string,
    payload: IdentityFieldPatchRequest,
  ): Promise<IdentityFieldPatchResponse> {
    const expectedVersion = requireMachineVersion()
    return runExclusiveMutation('patch_field', async () => {
      try {
        const requestId =
          `import-session-identity-patch-${sessionId}-${crypto.randomUUID()}`
        setLastPatchRequestId(requestId)
        setLastMutationRequestId(requestId)
        const next = await audienceImportSessionsApi.patchIdentityField(
          sessionId,
          canonicalId,
          { ...payload, expectedVersion },
          requestId,
        )
        const nextShell = mergeIdentityPatchIntoShell(shellQuery.data, next)
        if (nextShell) {
          void shellQuery.mutate(nextShell, { revalidate: false })
        }
        if (next.routeAck) {
          await setCachedFlowSnapshot(
            next.routeAck.flowSnapshot,
            'patch_field_response',
          )
        } else {
          await applyMachineVersionFromIdentityPatch(
            next.machineVersion,
            'patch_field_machine_version',
          )
        }
        return next
      } catch (error) {
        await Promise.all([
          revalidateShell('create_failure'),
          revalidateFlowSnapshot('create_failure'),
        ])
        await refetchOnConflict(error)
        throw error
      }
    })
  }

  async function completeRequiredFieldsStep(): Promise<AudienceImportRouteAck> {
    const expectedVersion = requireMachineVersion()
    return runExclusiveMutation('complete_required_fields', async () => {
      try {
        const ack = await audienceImportSessionsApi.completeRequiredFieldsStep(
          sessionId,
          expectedVersion,
        )
        await cacheRouteAck(ack, 'complete_required_fields_response')
        return ack
      } catch (error) {
        await refetchOnConflict(error)
        throw error
      }
    })
  }

  async function discard() {
    const expectedVersion = requireMachineVersion()
    return runExclusiveMutation('discard', async () => {
      try {
        await audienceImportSessionsApi.discard(
          sessionId,
          expectedVersion,
        )
        await Promise.all([
          shellQuery.mutate(undefined, { revalidate: false }),
          syncFlowSnapshotForMutation('discard'),
        ])
      } catch (error) {
        await refetchOnConflict(error)
        throw error
      }
    })
  }

  return {
    shell: shellQuery.data ?? null,
    error: shellQuery.error as Error | undefined,
    isLoading: shellQuery.isLoading,
    isValidating: shellQuery.isValidating,
    isRealtimeConnected,
    lastPatchRequestId,
    activeMutation,
    isMutating: activeMutation !== null,
    isPatching: activeMutation === 'patch_field',
    isCompletingRequiredFields: activeMutation === 'complete_required_fields',
    isDiscarding: activeMutation === 'discard',
    patchField,
    completeRequiredFieldsStep,
    discard,
    mutate: shellQuery.mutate,
  }
}
